/**
 * Conservative Two-Pass CA solver, V2. PASS 1 + TRANSPORT-ONLY PASS 2 (C2) +
 * FULL SOURCE/SINK PASS 2 (C3) + PRODUCTION PING-PONG `advance()` (C4) +
 * `implements FloodSolver` (C5) — compiler-enforced, no cast, no escape hatch.
 *
 * WHAT THIS PHASE DELIVERS
 *   current depth + hydraulic bed
 *     -> hydraulic surface
 *     -> exact donor selection
 *     -> candidate directional transfer terms
 *     -> donor-global limiter
 *     -> RGBA32Float transfer texture                              (PASS 1)
 *     -> incoming/outgoing gather, single coefficient application
 *     -> transport-only depth field, written to a SEPARATE
 *        validation texture (`transportOnlyOut`), never to
 *        `depthTextureA/B`                                          (PASS 2, C2)
 *     -> + rainfall, - physically capped infiltration, written to a THIRD
 *        validation texture (`sourceSinkOut`)                        (PASS 2, C3)
 *     -> the SAME source/sink kernel body, written DIRECTLY to the
 *        opposite depth texture (A<->B), flipped exactly once per
 *        physical substep, via `advance()`                           (PASS 2, C4)
 *     -> `outputs.depth`, a LIVE getter over the same ping-pong state
 *        `depthTexture` already exposes — one stable object, built once,
 *        never reallocated on access                                  (C5)
 *
 * `runPass2TransportOnly` and `runPass2Full` still exist and are UNCHANGED —
 * isolated validation paths, never called by `advance()` and not part of the
 * `FloodSolver` interface.
 *
 * PHASE 3 — `maxDepth`/`arrivalTime`/`duration`/`flux` are each independently
 * optional, chosen at construction via `analysis` and fixed for the instance's
 * lifetime; `outputs` advertises exactly the ones enabled — see
 * `FloodSolverOutputs` in `flood-solver.ts` for the full semantics of each, and
 * the ANALYSIS FIELD DERIVATIONS block at the end of this header for the
 * dimensional reasoning behind `flux`. With `analysis` absent, this solver is
 * byte-identical to the pre-Phase-3 depth-only implementation: zero new
 * textures, kernels, uniforms, or dispatches. Production selects V2 as the
 * default via `flood-solver-factory.ts`; nothing here changed that.
 *
 * V2-E1: `transportOnlyOut`/`sourceSinkOut` and their four kernels are now
 * built LAZILY, exactly once, on first use by a validation API — not
 * unconditionally in the constructor. A normal production instance that
 * only ever calls `advance()` never allocates them. See
 * `ensureValidationResources()`; the kernel bodies themselves are untouched.
 *
 * WHY TWO PASSES AT ALL. Not "two passes are more conservative" — that claim is
 * false for V1's kernel, whose per-face exchange is algebraically antisymmetric.
 * The measured reason is narrower and stronger: on the target adapter WGSL
 * `mix(a, b, t)` lowers to `a + t*(b - a)`, which is inexact at t=1. V1 selects
 * the donor depth with `mix(h_i, h_j, step(H_i, H_j))`, so the donating cell
 * (t=0, exact) and the receiving cell (t=1, inexact) can disagree about how much
 * water moved. Equal-and-opposite transfer is not STRUCTURALLY guaranteed.
 *
 * PASS 1 stores each transfer ONCE and PASS 2 will consume that same stored
 * value as both donor-outgoing and receiver-incoming, so conservation stops
 * depending on two cells independently evaluating expressions that are only
 * algebraically equal. That reason holds regardless of how often donor overdraw
 * turns up in production — which remains UNPROVEN: two real AOIs measured zero
 * susceptible cells.
 *
 * DONOR OVERDRAW IS NOT CLAIMED TO BE WIDESPREAD. It is mathematically
 * demonstrated and reproduced synthetically; its production prevalence is a
 * V2-D characterization question, not a justification available today.
 *
 * ---------------------------------------------------------------------------
 * UNITS — derived from the V1 expression, not chosen
 * ---------------------------------------------------------------------------
 *   candidate transfer term   max(0, H_i - H_j) * h_i        [m] * [m] = m^2
 *   transport coefficient     conductance * dt / cellSize^2  [m/s][s]/[m^2] = 1/m
 *   available depth           h_i                            m
 *   limiter scale             available / (coefficient * totalCandidate)   dimensionless
 *
 * depth transferred this step = stored term * coefficient  [m^2] * [1/m] = m
 *
 * The stored term is UNSCALED on purpose. A4 established that storing the
 * unscaled term and applying `((C*dt) * net) / (cellSize*cellSize)` once in
 * PASS 2 — exactly where and how V1 applies it — reproduces an exact-donor V1
 * bit-for-bit, 4096/4096. Scaling per-face in PASS 1 instead would reassociate
 * the arithmetic and lose that correspondence for nothing.
 *
 * The stored field remains INTERNAL NUMERICAL STATE — units m², not a
 * publishable engineering quantity by itself. `outputs.flux` (Phase 3, below)
 * is a SEPARATE, converted, public texture, never this one re-exposed.
 *
 * ---------------------------------------------------------------------------
 * ANALYSIS FIELD DERIVATIONS (Phase 3)
 * ---------------------------------------------------------------------------
 *
 * All four fields read either the POST-substep depth (what PASS 2 just wrote)
 * or the PRE-substep depth (what PASS 2 read, still intact) — never both, and
 * `duration` is the one field that deliberately reads PRE. Each scalar field
 * (`maxDepth`/`arrivalTime`/`duration`) is ping-ponged on depth's own parity:
 * TSL has no valid pattern for one kernel to both `textureLoad` and
 * `textureStore` the same texture, since the binding type is fixed by whichever
 * usage the node builder resolves first.
 *
 *   maxDepth[cell]  = max(previousMaxDepth[cell], postDepth[cell])
 *                     seeded from the initial depth field, not zero
 *
 *   arrivalTime[cell] = fp32(elapsedSimSeconds + dtSeconds)  the FIRST time
 *                       postDepth[cell] >= wetThresholdM, else unchanged
 *                     sentinel -1 while never crossed; seeded to 0 where the
 *                     initial depth is already >= wetThresholdM
 *
 *   duration[cell]  = previousDuration[cell] + (preDepth[cell] >= wetThresholdM ? dtSeconds : 0)
 *                     LEFT-ENDPOINT OCCUPANCY: credits the interval [t, t+dt)
 *                     by whether the cell was wet at the interval's START, so
 *                     a cell first crossing during substep k earns nothing for
 *                     the interval preceding its own `arrivalTime` — the two
 *                     fields are deliberately coherent, not independently
 *                     defined
 *
 * flux (dimensional derivation, from the transport equations above):
 *
 *   depth transferred this substep, face f = limitedTransferTermM2_f * commonScale   [m]
 *   Volume_f [m^3] = (limitedTransferTermM2_f * commonScale) * cellSize^2
 *                  = limitedTransferTermM2_f * conductance * dt / cellSize^2 * cellSize^2
 *                  = limitedTransferTermM2_f * conductance * dt
 *   Q_f [m^3/s]    = Volume_f / dt = limitedTransferTermM2_f * conductance
 *
 * `dt` cancels algebraically, but `limitedTransferTermM2_f` already carries a
 * `limiterScale` factor that is itself proportional to `1/dt` whenever the
 * donor-global limiter is active — so `Q_f` is dt-INDEPENDENT only while the
 * limiter is inactive for that donor; when active, `Q_f` is the true,
 * dt-dependent, capped discharge that occurred for that substep's actual `dt`.
 * Both are the honest dimensional meaning of the current equations, not a
 * defect in either regime (Phase-3 3E, Proofs A/B, verified this on real
 * hardware down to exact bit/dimensional equality). `fluxConversion` runs once
 * per `advance()` call, never per substep — `transferTextureRef` has no
 * ping-pong and is already fully overwritten by the final substep's PASS 1, so
 * nothing extra is needed to represent "the final physical substep only".
 */
import * as THREE from 'three/webgpu'
import {
  Fn, instanceIndex, uniform, ivec2, uvec2, vec4, float, select, step,
  max as tslMax, min as tslMin, textureStore, textureLoad,
} from 'three/tsl'

import { cellOfIndex, clampCellComponent, loadCell, createScalarField } from './flood-fields'
import type { FloodAnalysisOptions, FloodForcing, FloodSolver, FloodSolverOutputs } from './flood-solver'
import { DEFAULT_ANALYSIS_WET_THRESHOLD_M } from './flood-solver'

/**
 * Channel assignment for the transfer texture. Written down once, here, because
 * an axis transposition produces a plausible but mirrored flood rather than an
 * obvious failure. PASS 2 will read the OPPOSITE channel of the neighbour:
 * r <-> b for north/south, g <-> a for east/west.
 */
export const TRANSFER_CHANNEL = { north: 0, east: 1, south: 2, west: 3 } as const

function buildComputeKernel(body: () => void, cellCount: number) {
  return Fn(body)().compute(cellCount)
}
type ComputeKernel = ReturnType<typeof buildComputeKernel>

export interface ConservativeTwoPassSolverV2Options {
  gridSize: number
  cellSizeM: number
  /** Static hydraulic floor. Referenced, never disposed here — the caller owns it. */
  bedTexture: THREE.Texture
  /** Same default and meaning as V1. Unchanged on purpose: V2-B alters the formulation, not the physics constants. */
  conductance?: number
  initialDepthM?: number
  initialDepthGrid?: Float32Array
  /**
   * Phase 3. Which analysis outputs to compute, fixed for this instance's
   * lifetime. Omitted (or all-false) means the solver behaves exactly as it did
   * before Phase 3: no analysis textures, no analysis kernels, no analysis
   * uniforms, no clock, no extra dispatches.
   */
  analysis?: FloodAnalysisOptions
}

/** Resolved, immutable view of {@link FloodAnalysisOptions} for one instance. */
interface ResolvedAnalysis {
  maxDepth: boolean
  arrivalTime: boolean
  duration: boolean
  flux: boolean
  /** Only meaningful when `arrivalTime || duration`; validated at construction in that case. */
  wetThresholdM: number
  /** True when at least one capability is on — the single check the hot path needs. */
  any: boolean
}

/**
 * Rejects a threshold that would silently corrupt every arrival/duration value
 * for the whole run. Zero is rejected rather than clamped: the comparison is
 * inclusive (`depth >= threshold`), so zero would mark genuinely dry cells wet.
 */
function resolveAnalysis(options: FloodAnalysisOptions | undefined): ResolvedAnalysis {
  const maxDepth = options?.maxDepth === true
  const arrivalTime = options?.arrivalTime === true
  const duration = options?.duration === true
  const flux = options?.flux === true
  const needsThreshold = arrivalTime || duration
  const wetThresholdM = options?.wetThresholdM ?? DEFAULT_ANALYSIS_WET_THRESHOLD_M

  if (needsThreshold && !(Number.isFinite(wetThresholdM) && wetThresholdM > 0)) {
    throw new Error(
      `ConservativeTwoPassSolverV2: analysis.wetThresholdM must be a finite number > 0, received ${wetThresholdM}. `
      + 'It is the physical wet threshold shared by arrivalTime and duration; a non-positive or non-finite value '
      + 'would make both outputs meaningless, so it is rejected rather than clamped.',
    )
  }

  return {
    maxDepth,
    arrivalTime,
    duration,
    flux,
    wetThresholdM,
    any: maxDepth || arrivalTime || duration || flux,
  }
}

export class ConservativeTwoPassSolverV2 implements FloodSolver {
  readonly gridSize: number
  /**
   * Identical formula to V1, deliberately. Whether the two-pass formulation
   * permits a larger dt is a V2-E measurement; changing it here would confound
   * every comparison the phase exists to make.
   */
  readonly maxStableDt: number

  private readonly bedTextureRef: THREE.Texture
  private readonly depthTextureA: THREE.StorageTexture
  private readonly depthTextureB: THREE.StorageTexture
  private readonly seedTexture: THREE.DataTexture
  /**
   * The PASS-1 output. ONE texture, no ping-pong and no clear pass: PASS 1
   * writes every channel of every texel unconditionally, so nothing stale can
   * survive, and the write-then-read and read-then-write hazards across a
   * substep are both handled by dispatch ordering within a single compute pass.
   */
  private readonly transferTextureRef: THREE.StorageTexture
  /**
   * V2-E1. VALIDATION-ONLY resources — `transportOnlyOut`/`sourceSinkOut` and
   * their four kernels (`pass2TransportOnlyFrom*`/`pass2FullFrom*`) are
   * unused by production `advance()` (traced, not assumed: `advance()` only
   * ever touches `pass1From*`/`pass2FullAtoB`/`pass2FullBtoA`). Measured at
   * ~22% of V2's solver-owned GPU memory in every V2-E benchmark grid, and
   * built/allocated regardless of whether any validation API is ever called.
   * `ensureValidationResources()` creates this whole group lazily, exactly
   * once, on first use by any of `runPass2TransportOnly`/`runPass2Full`/
   * `readTransportOnlyDepthBuffer`/`readSourceSinkDepthBuffer` — never from
   * the constructor, `advance()`, `reset()`, or the `outputs`/`depthTexture`
   * getters. Deliberately NOT `import.meta.env.DEV`-gated: the solver has no
   * business knowing whether it is running in dev or production, and these
   * APIs must stay usable wherever explicitly invoked.
   */
  private transportOnlyOut?: THREE.StorageTexture
  private sourceSinkOut?: THREE.StorageTexture

  private readonly pass1FromA: ComputeKernel
  private readonly pass1FromB: ComputeKernel
  /**
   * V2-C2. Transport ONLY: no rainfall, no infiltration uniforms exist for
   * these kernels at all — not dormant zero-valued uniforms, genuinely
   * absent. Source/sink is C3's job. VALIDATION-ONLY — see
   * `ensureValidationResources()`.
   */
  private pass2TransportOnlyFromA?: ComputeKernel
  private pass2TransportOnlyFromB?: ComputeKernel
  /**
   * V2-C3. Same transport arithmetic as `pass2TransportOnlyFrom*` — copied,
   * not re-derived — extended with rainfall and physically capped
   * infiltration, transcribed verbatim from `referencePass2`
   * (conservative-two-pass-solver-v2-reference.ts), already proven bit-exact
   * against the C1 unit tests. VALIDATION-ONLY — see
   * `ensureValidationResources()`.
   */
  private pass2FullFromA?: ComputeKernel
  private pass2FullFromB?: ComputeKernel
  /**
   * V2-C4. PRODUCTION targets — same kernel body as `pass2FullFrom*`, built
   * with `depthTextureB`/`depthTextureA` as the write target instead of the
   * validation-only `sourceSinkOut`. `advance()` is the only caller.
   */
  private readonly pass2FullAtoB: ComputeKernel
  private readonly pass2FullBtoA: ComputeKernel
  private readonly seedDepth: ComputeKernel
  /**
   * V2-C5. ONE stable object, built once in the constructor — never
   * reallocated on access (`get outputs()` returns this same reference every
   * time). `depth` is a live getter closing over `this.depthTexture`, the
   * exact pattern `DonorCellSolverV1` already uses, so it tracks every
   * ping-pong flip and every `reset()` without a consumer ever needing to
   * re-fetch `solver.outputs`. Phase 3: `flux`/`maxDepth`/`arrivalTime`/
   * `duration` are added via `Object.defineProperty` (never a conditional
   * object spread, which would invoke the getter at construction time and
   * freeze the field to whichever ping-pong side happened to be current then)
   * — each present iff the matching `analysis` flag was enabled.
   */
  private readonly outputsObject: FloodSolverOutputs

  private readonly dtUniform = uniform(1 / 30)
  /** Already-integrated depth for this substep, exactly as `FloodForcing` defines it. Never multiplied by dt here. */
  private readonly rainfallUniform = uniform(0)
  /** Already-integrated REQUESTED depth; PASS 2 caps it to what is physically available post-rain. */
  private readonly infiltrationUniform = uniform(0)
  /**
   * Set once from constructor options, never reassigned afterward (unlike
   * `dtUniform`/`rainfallUniform`/`infiltrationUniform`, which `advance()`
   * updates every call) — promoted from a constructor-local `const` to an
   * instance field so `ensureValidationResources()` can build the lazy
   * validation kernels after construction using the SAME uniform objects the
   * eager production kernels already close over.
   */
  private readonly conductanceUniform: ReturnType<typeof uniform>
  private readonly cellSizeUniform: ReturnType<typeof uniform>

  /**
   * Phase 3. Which analysis capabilities this instance computes. Resolved once
   * in the constructor and never mutated: `maxDepth`/`arrivalTime`/`duration`
   * are historical, so enabling one mid-run could not reconstruct the history
   * it missed and would silently under-report.
   */
  private readonly analysis: ResolvedAnalysis

  /**
   * Phase 3 analysis fields. Each scalar field is PING-PONGED on the same
   * parity as depth (`currentIsA`), because a kernel cannot both `textureLoad`
   * and `textureStore` the same texture: the binding resolves to either
   * `texture_2d<f32>` or `texture_storage_2d<...>`, and neither supports the
   * other operation. Every one of these carries its own previous value forward
   * (max-with-previous, sentinel check, accumulate), so all three need two
   * sides. Absent entirely unless the matching capability is enabled.
   */
  private maxDepthA?: THREE.StorageTexture
  private maxDepthB?: THREE.StorageTexture
  private arrivalTimeA?: THREE.StorageTexture
  private arrivalTimeB?: THREE.StorageTexture
  private durationA?: THREE.StorageTexture
  private durationB?: THREE.StorageTexture
  /**
   * Flux needs NO ping-pong: it is fully overwritten from `transferTextureRef`
   * every time the conversion kernel runs and never reads its own prior value.
   */
  private fluxTexture?: THREE.StorageTexture

  /**
   * One immutable threshold uniform, created only when `arrivalTime` or
   * `duration` is on. A uniform rather than a TSL literal on purpose: V2-A
   * measured that literals get constant-folded at double precision by the
   * shader compiler, which silently changed what a probe was measuring.
   */
  private readonly wetThresholdUniform?: ReturnType<typeof uniform>

  /**
   * Solver-owned simulation clock, in f64 on the CPU. Created only when
   * `arrivalTime` is enabled — nothing else needs absolute time (`duration`
   * accumulates `dtUniform`, which `advance()` already sets per call).
   *
   * The value written into the R32Float arrival texture is necessarily
   * `fp32(elapsedSimSeconds + dtSeconds)`; that rounding IS the published
   * representation, and CPU references must compare against `Math.fround` of
   * the same sum rather than an unrounded double.
   */
  private elapsedSimSeconds = 0
  private readonly elapsedTimeUniform?: ReturnType<typeof uniform>

  /**
   * One dedicated seed kernel per enabled field, never folded into
   * `seedDepth`. `seedDepth` already writes 2 storage textures; adding three
   * ping-pong pairs to it would put 8 write targets in a single kernel. These
   * run once per seeding, alongside `seedDepth`, so the cost is one-time.
   */
  private readonly seedMaxDepth?: ComputeKernel
  private readonly seedArrivalTime?: ComputeKernel
  private readonly seedDuration?: ComputeKernel
  private readonly clearFlux?: ComputeKernel
  /**
   * Phase 3E. No A/B pair — flux is fully overwritten from
   * `transferTextureRef` every time this runs and never reads its own prior
   * value. One kernel, run once per `advance()` call rather than once per
   * substep, after the last physical substep's PASS 1.
   */
  private readonly fluxConversion?: ComputeKernel

  /**
   * Phase 3C. One kernel per ping-pong direction, mirroring `pass1FromA`/
   * `pass1FromB`. `maxDepthAtoB` runs in a substep that STARTED on A: it reads
   * `maxDepthA` and the post-substep depth (which PASS 2 wrote into
   * `depthTextureB`), and writes `maxDepthB`. Built only when the capability
   * is on.
   */
  private readonly maxDepthAtoB?: ComputeKernel
  private readonly maxDepthBtoA?: ComputeKernel

  /**
   * Phase 3D. `arrivalAtoB` runs in a substep that started on A: reads
   * `arrivalTimeA` and the POST-substep depth (`depthTextureB`), writes
   * `arrivalTimeB`.
   */
  private readonly arrivalAtoB?: ComputeKernel
  private readonly arrivalBtoA?: ComputeKernel
  /**
   * Phase 3D. Duration reads the PRE-substep depth — the texture the substep
   * STARTED on, still intact because PASS 2 wrote the other one. That is the
   * left-endpoint occupancy rule, and it is the one place an analysis kernel
   * deliberately does not look at the freshest depth.
   */
  private readonly durationAtoB?: ComputeKernel
  private readonly durationBtoA?: ComputeKernel

  private currentIsA = true
  private depthSeeded = false

  constructor(options: ConservativeTwoPassSolverV2Options) {
    const { gridSize, cellSizeM, bedTexture } = options
    this.gridSize = gridSize
    this.bedTextureRef = bedTexture
    const cellCount = gridSize * gridSize
    const conductance = options.conductance ?? 12
    // Throws on an invalid threshold BEFORE any GPU resource is allocated, so a
    // rejected construction leaks nothing.
    this.analysis = resolveAnalysis(options.analysis)

    const MAX_EXPECTED_DEPTH_M = 2.0
    const STABILITY_SAFETY_FACTOR = 0.25
    this.maxStableDt = (cellSizeM * cellSizeM) / (4 * conductance * MAX_EXPECTED_DEPTH_M) * STABILITY_SAFETY_FACTOR

    const initialDepth = options.initialDepthGrid
      ? Float32Array.from(options.initialDepthGrid)
      : new Float32Array(cellCount)
    if (!options.initialDepthGrid && options.initialDepthM) initialDepth.fill(options.initialDepthM)
    this.seedTexture = createScalarField(initialDepth, gridSize, 'V2DepthSeed')

    const makeDepthTexture = (name: string) => {
      const tex = new THREE.StorageTexture(gridSize, gridSize)
      tex.name = name
      // StorageTexture inherits RGBAFormat + UnsignedByteType from Texture, i.e.
      // rgba8unorm. Both MUST be set or the field silently becomes 8-bit.
      tex.format = THREE.RedFormat
      tex.type = THREE.FloatType
      tex.magFilter = THREE.LinearFilter
      tex.minFilter = THREE.LinearFilter
      tex.generateMipmaps = false
      tex.wrapS = THREE.ClampToEdgeWrapping
      tex.wrapT = THREE.ClampToEdgeWrapping
      return tex
    }

    this.depthTextureA = makeDepthTexture('V2DepthA')
    this.depthTextureB = makeDepthTexture('V2DepthB')

    const transfer = new THREE.StorageTexture(gridSize, gridSize)
    transfer.name = 'V2Transfer'
    transfer.format = THREE.RGBAFormat
    transfer.type = THREE.FloatType
    transfer.generateMipmaps = false
    transfer.minFilter = THREE.NearestFilter
    transfer.magFilter = THREE.NearestFilter
    transfer.wrapS = THREE.ClampToEdgeWrapping
    transfer.wrapT = THREE.ClampToEdgeWrapping
    this.transferTextureRef = transfer

    // V2-E1: `transportOnlyOut`/`sourceSinkOut` are built lazily by
    // `ensureValidationResources()` on first validation use, not here — see
    // that method and the fields' own doc comment.

    const seedTexture = this.seedTexture
    const depthTextureA = this.depthTextureA
    const depthTextureB = this.depthTextureB

    this.seedDepth = buildComputeKernel(() => {
      const cell = cellOfIndex(instanceIndex, gridSize)
      const v = loadCell(seedTexture, cell)
      const store = uvec2(cell.x, cell.y)
      textureStore(depthTextureA, store, vec4(v, 0, 0, 1)).toWriteOnly()
      textureStore(depthTextureB, store, vec4(v, 0, 0, 1)).toWriteOnly()
    }, cellCount)

    this.conductanceUniform = uniform(conductance)
    this.cellSizeUniform = uniform(cellSizeM)
    // Local aliases so `buildPass1`'s existing TSL body below (unchanged by
    // V2-E1) keeps referencing the same bare names it always has.
    const conductanceUniform = this.conductanceUniform
    const cellSizeUniform = this.cellSizeUniform
    const dtUniform = this.dtUniform
    const rainfallUniform = this.rainfallUniform
    const infiltrationUniform = this.infiltrationUniform

    /**
     * PASS 1. Reads bed + one depth texture, writes the transfer texture.
     *
     * Built once per ping-pong direction so a dispatch never reads a resource
     * anything in the same dispatch writes.
     */
    const buildPass1 = (readDepth: THREE.Texture) => buildComputeKernel(() => {
      const cell = cellOfIndex(instanceIndex, gridSize)
      const x = cell.x
      const y = cell.y

      const loadBed = (cx: typeof x, cy: typeof y) => loadCell(this.bedTextureRef, ivec2(cx, cy))
      const loadDepth = (cx: typeof x, cy: typeof y) => loadCell(readDepth, ivec2(cx, cy))

      const h = loadDepth(x, y).toVar()
      const surface = loadBed(x, y).add(h).toVar()

      const leftX = clampCellComponent(x.sub(1), gridSize)
      const rightX = clampCellComponent(x.add(1), gridSize)
      const bottomY = clampCellComponent(y.sub(1), gridSize)
      const topY = clampCellComponent(y.add(1), gridSize)

      /**
       * EXPLICIT CLOSED BOUNDARY, not inherited from index clamping.
       *
       * V1's edge is no-flow by accident: the clamped neighbour aliases the
       * cell itself, so `H_i - H_i` is exactly 0 and the term vanishes. That
       * accident does not survive PASS 2's gather, where the clamped lookup
       * returns this cell's texel and reading the opposite channel yields its
       * own outgoing term — a nonzero value with no source. Masking here makes
       * the closed boundary a property of the formulation rather than of the
       * addressing, and keeps PASS 1 and PASS 2 consistent about it.
       *
       * Branchless: `finite * 0` is exactly 0, and it keeps texture reads out
       * of a TSL if/else arm — the lowering that once made every debug mode in
       * the renderer read zero.
       */
      const fx = x.toFloat()
      const fy = y.toFloat()
      const last = float(gridSize - 1)
      const maskNorth = step(float(1), last.sub(fy))
      const maskSouth = step(float(1), fy)
      const maskEast = step(float(1), last.sub(fx))
      const maskWest = step(float(1), fx)

      /**
       * EXACT donor selection — `max(0, H_i - H_j)`, no `mix`.
       *
       * An outgoing term exists only when this cell's surface is the higher of
       * the two, which is V1's t=0 case, whose donor is this cell's own depth
       * and is exact under every mix lowering. V2 never evaluates the t=1 arm,
       * which is precisely the arm the probes found inexact. The reciprocal
       * term is produced by the neighbour's own t=0 evaluation.
       *
       * V1's defect is NOT reproduced for the sake of parity. The design target
       * is the intended transfer rule, not V1's accidental arithmetic.
       */
      const candidateTo = (nx: typeof x, ny: typeof y, mask: ReturnType<typeof float>) => {
        const surfN = loadBed(nx, ny).add(loadDepth(nx, ny))
        return tslMax(float(0), surface.sub(surfN)).mul(h).mul(mask)
      }

      const qNorth = candidateTo(x, topY, maskNorth).toVar()
      const qSouth = candidateTo(x, bottomY, maskSouth).toVar()
      const qEast = candidateTo(rightX, y, maskEast).toVar()
      const qWest = candidateTo(leftX, y, maskWest).toVar()

      /**
       * DONOR-GLOBAL LIMITER — the central V2 feature.
       *
       * The candidates are m^2; the water available is a depth. Converting the
       * total once with the same transport coefficient PASS 2 will apply makes
       * the comparison dimensionally consistent:
       *
       *   totalCandidateDepth = (conductance * dt) * totalCandidate / cellSize^2   [m]
       *
       * `available` is the OLD physical depth alone. The candidates were derived
       * from a surface built on that depth, so that is the water the gradient
       * actually described; it also matches V1's own bound, whose clamp uses
       * `h` and nothing else. Rainfall is deliberately excluded — it has not
       * been added when transport is evaluated, and including it would make the
       * limiter depend on forcing, so the same bed and depth would limit
       * differently under rain.
       *
       * `select` rather than `min(1, available/total)`: the division is then
       * only evaluated when `total > available`, which implies `total > 0`, so
       * a dry cell cannot produce 0/0 = NaN. Both arms are pure arithmetic on
       * already-loaded values, so the if/else lowering carries no texture-read
       * hazard.
       *
       * When the limiter is inactive the scale is exactly 1.0 and `q * 1.0 == q`
       * bit-for-bit, which is what keeps the unlimited case identical to the
       * exact-donor reference.
       */
      const totalCandidate = qNorth.add(qSouth).add(qEast).add(qWest).toVar()
      const totalCandidateDepth = conductanceUniform.mul(dtUniform).mul(totalCandidate)
        .div(cellSizeUniform.mul(cellSizeUniform))
      const scale = select(totalCandidateDepth.greaterThan(h), h.div(totalCandidateDepth), float(1)).toVar()

      textureStore(transfer, uvec2(x, y), vec4(
        qNorth.mul(scale), qEast.mul(scale), qSouth.mul(scale), qWest.mul(scale),
      )).toWriteOnly()
    }, cellCount)

    this.pass1FromA = buildPass1(depthTextureA)
    this.pass1FromB = buildPass1(depthTextureB)

    /**
     * V2-C4 — PRODUCTION source/sink targets. Bit-for-bit the SAME kernel
     * body `buildPass2FullKernel` builds for the C3 validation path (below,
     * built lazily) — the only difference is the write target: the OPPOSITE
     * depth texture, directly. No intermediate copy, no third dispatch.
     * `advance()` is the only caller.
     */
    this.pass2FullAtoB = this.buildPass2FullKernel(depthTextureA, depthTextureB)
    this.pass2FullBtoA = this.buildPass2FullKernel(depthTextureB, depthTextureA)

    // ---- Phase 3B: analysis resources + seed kernels -----------------------
    //
    // Nothing below runs when `analysis` is absent: no texture, no kernel, no
    // uniform. That is what makes the disabled path byte-identical to the
    // pre-Phase-3 solver rather than merely "close to it".
    if (this.analysis.arrivalTime || this.analysis.duration) {
      this.wetThresholdUniform = uniform(this.analysis.wetThresholdM)
    }
    if (this.analysis.arrivalTime) {
      this.elapsedTimeUniform = uniform(0)
    }

    if (this.analysis.maxDepth) {
      this.maxDepthA = makeDepthTexture('V2MaxDepthA')
      this.maxDepthB = makeDepthTexture('V2MaxDepthB')
      const a = this.maxDepthA
      const b = this.maxDepthB
      // Seeded from the INITIAL DEPTH FIELD, not zero: water present at t=0 is
      // a legitimate observation of the maximum, and seeding zero would let a
      // field that only ever recedes report a maximum it never reached.
      this.seedMaxDepth = buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        const v = loadCell(seedTexture, cell)
        const store = uvec2(cell.x, cell.y)
        textureStore(a, store, vec4(v, 0, 0, 1)).toWriteOnly()
        textureStore(b, store, vec4(v, 0, 0, 1)).toWriteOnly()
      }, cellCount)

      // 3 storage textures: previous side (read), post-substep depth (read),
      // next side (write). `postDepth` is the texture PASS 2 wrote in this
      // same substep, which is the OPPOSITE of the one the substep started on.
      const buildMaxDepth = (
        previous: THREE.StorageTexture, postDepth: THREE.StorageTexture, next: THREE.StorageTexture,
      ) => buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        const prev = loadCell(previous, cell)
        const post = loadCell(postDepth, cell)
        textureStore(next, uvec2(cell.x, cell.y), vec4(tslMax(prev, post), 0, 0, 1)).toWriteOnly()
      }, cellCount)

      this.maxDepthAtoB = buildMaxDepth(a, this.depthTextureB, b)
      this.maxDepthBtoA = buildMaxDepth(b, this.depthTextureA, a)
    }

    if (this.analysis.arrivalTime) {
      this.arrivalTimeA = makeDepthTexture('V2ArrivalTimeA')
      this.arrivalTimeB = makeDepthTexture('V2ArrivalTimeB')
      const a = this.arrivalTimeA
      const b = this.arrivalTimeB
      const threshold = this.wetThresholdUniform!
      // A cell already wet at t=0 arrived at t=0 — recorded here rather than
      // left for the first substep to discover, which would report `dt` for
      // water that was there before the run began. Everything else starts at
      // the -1 sentinel; 0 is unavailable for "never" precisely because
      // arrival at 0 is a real answer.
      this.seedArrivalTime = buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        const v = loadCell(seedTexture, cell)
        const store = uvec2(cell.x, cell.y)
        // `step(threshold, v) - 1` rather than a select: WGSL step is
        // `v >= threshold ? 1 : 0`, exactly the inclusive comparison the
        // frozen semantics call for, and it is the same branchless idiom
        // PASS 1 already uses for boundary masks — no TSL if/else statement,
        // so no untaken-arm hazard.
        //
        // MEASURED: the wet branch lands on NEGATIVE zero on this adapter,
        // under both this form and a select between literals 0 and -1. `1 - 1`
        // is +0 in IEEE round-to-nearest, so the sign almost certainly comes
        // from the shader compiler rewriting `a - 1` as `-(1 - a)`; `-(+0)` is
        // -0. Chasing it further would be arguing with the optimizer about a
        // sign bit.
        //
        // It is harmless and, importantly, DETERMINISTIC. -0 === 0, the `< 0`
        // sentinel test reads -0 as "arrived" (correct), and JSON serialises it
        // as 0. Because every wet cell takes the same path every run, two runs
        // still compare bit-identically. Tests assert numeric equality here
        // rather than encoding identity — that is not a tolerance, ±0 are the
        // same real number.
        const seeded = step(threshold, v).sub(1)
        textureStore(a, store, vec4(seeded, 0, 0, 1)).toWriteOnly()
        textureStore(b, store, vec4(seeded, 0, 0, 1)).toWriteOnly()
      }, cellCount)

      // 3 storage textures: previous arrival (read), POST-substep depth
      // (read), next arrival (write). Branchless throughout — `select` lowers
      // to a real if/else statement in TSL, and there is no reason to risk
      // that when a mask does the job.
      const elapsed = this.elapsedTimeUniform!
      const buildArrival = (
        previous: THREE.StorageTexture, postDepth: THREE.StorageTexture, next: THREE.StorageTexture,
      ) => buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        const prev = loadCell(previous, cell)
        const post = loadCell(postDepth, cell)
        // FIRST arrival only: a cell already holding a timestamp keeps it, so a
        // field that dries and re-wets never overwrites its own history.
        //
        // `step(0, prev)` is 1 when prev >= 0, i.e. "already arrived". It reads
        // the -0 the seed produces for initially-wet cells as arrived, which is
        // correct — and it must be `>= 0` rather than a negated `< 0` test for
        // exactly that reason: `-(-0) >= 0` would misclassify a t=0 arrival as
        // still-dry and let it be re-stamped later.
        const alreadyArrived = step(0, prev)
        const notYetArrived = float(1).sub(alreadyArrived)
        const nowWet = step(threshold, post)
        const takeStamp = notYetArrived.mul(nowWet)
        // PRODUCT form, not `prev + takeStamp * (elapsed - prev)`.
        //
        // The fma-shaped alternative is the same trap V2-A measured in V1's
        // `mix`: with prev = -1 it evaluates `-1 + (elapsed + 1)`, which is not
        // exactly `elapsed` once elapsed + 1 rounds. Here each branch is exact —
        // `prev*0 + elapsed*1` is elapsed, `prev*1 + elapsed*0` is prev — so the
        // recorded timestamp is exactly the fp32 value the CPU reference
        // predicts, and the comparison stays bit-for-bit rather than tolerant.
        const keepPrev = float(1).sub(takeStamp)
        const stamped = prev.mul(keepPrev).add(elapsed.mul(takeStamp))
        textureStore(next, uvec2(cell.x, cell.y), vec4(stamped, 0, 0, 1)).toWriteOnly()
      }, cellCount)

      this.arrivalAtoB = buildArrival(a, this.depthTextureB, b)
      this.arrivalBtoA = buildArrival(b, this.depthTextureA, a)
    }

    if (this.analysis.duration) {
      this.durationA = makeDepthTexture('V2DurationA')
      this.durationB = makeDepthTexture('V2DurationB')
      const a = this.durationA
      const b = this.durationB
      // Always zero, even for initially-wet cells: no simulation time has
      // elapsed yet, and duration counts elapsed time, not wetness.
      this.seedDuration = buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        const store = uvec2(cell.x, cell.y)
        textureStore(a, store, vec4(0, 0, 0, 1)).toWriteOnly()
        textureStore(b, store, vec4(0, 0, 0, 1)).toWriteOnly()
      }, cellCount)

      // 3 storage textures: previous duration (read), PRE-substep depth
      // (read), next duration (write).
      //
      // LEFT-ENDPOINT OCCUPANCY: credit the whole interval [t, t+dt) iff the
      // cell was wet at its START. That is what keeps duration coherent with
      // arrival's end-of-substep timestamp — a cell first crossing at the end
      // of a substep records arrival there and earns no credit for the interval
      // that preceded its own arrival. A cell wet at the start and dry at the
      // end still earns the full dt; that is deliberate discrete integration,
      // and the error it admits is bounded by one substep.
      //
      // `prev + wet*dt` is exact in both branches (wet*dt is either +0 or dt),
      // so it matches the reference's stepwise fp32 accumulation exactly.
      const durationThreshold = this.wetThresholdUniform!
      const dt = this.dtUniform
      const buildDuration = (
        previous: THREE.StorageTexture, preDepth: THREE.StorageTexture, next: THREE.StorageTexture,
      ) => buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        const prev = loadCell(previous, cell)
        const pre = loadCell(preDepth, cell)
        const wet = step(durationThreshold, pre)
        textureStore(next, uvec2(cell.x, cell.y), vec4(prev.add(wet.mul(dt)), 0, 0, 1)).toWriteOnly()
      }, cellCount)

      // Note the depth argument: the texture the substep STARTED on, i.e. the
      // one PASS 2 read rather than the one it wrote.
      this.durationAtoB = buildDuration(a, this.depthTextureA, b)
      this.durationBtoA = buildDuration(b, this.depthTextureB, a)
    }

    if (this.analysis.flux) {
      const flux = new THREE.StorageTexture(gridSize, gridSize)
      flux.name = 'V2Flux'
      flux.format = THREE.RGBAFormat
      flux.type = THREE.FloatType
      flux.generateMipmaps = false
      flux.minFilter = THREE.NearestFilter
      flux.magFilter = THREE.NearestFilter
      flux.wrapS = THREE.ClampToEdgeWrapping
      flux.wrapT = THREE.ClampToEdgeWrapping
      this.fluxTexture = flux
      // Cleared on every (re-)seed so a fresh run never publishes the previous
      // run's flux. Unlike the scalar fields this is not an initial condition —
      // flux only means anything once transport has actually run — but leaving
      // it stale across a reset would be indistinguishable from a real value.
      this.clearFlux = buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        textureStore(flux, uvec2(cell.x, cell.y), vec4(0, 0, 0, 0)).toWriteOnly()
      }, cellCount)

      // 1 storage write + 1 sampled read. `Q_f = storedTransfer * conductance`
      // — see the file header derivation. dt cancels out of the rate entirely,
      // so this kernel needs no dt uniform and is correct for any dt the
      // substep that produced `transferTextureRef` happened to use.
      const transferRef = this.transferTextureRef
      const conductanceForFlux = this.conductanceUniform
      this.fluxConversion = buildComputeKernel(() => {
        const cell = cellOfIndex(instanceIndex, gridSize)
        // `loadCell` hardcodes `.r`, correct for the scalar depth/analysis
        // fields but not for the RGBA transfer texture — this reads all four
        // channels directly, matching how PASS 2 itself reads `transfer`.
        const t = textureLoad(transferRef, ivec2(cell.x, cell.y))
        textureStore(flux, uvec2(cell.x, cell.y), t.mul(conductanceForFlux)).toWriteOnly()
      }, cellCount)
    }

    // Arrow closes over `this`; the literal's own getter would not.
    //
    // A key appears here only once its update kernel exists and is proven, so
    // presence-as-capability never lies: 3C added maxDepth, 3D adds
    // arrivalTime and duration, 3E adds flux. Each getter is live and selects
    // the current ping-pong side from the same parity `depth` uses, so a
    // consumer never has to re-fetch `solver.outputs` after a step or a reset.
    const currentDepth = () => this.depthTexture
    const outputs: FloodSolverOutputs = {
      get depth(): THREE.Texture { return currentDepth() },
    }

    // `Object.defineProperty`, NOT a conditional object spread. Spreading
    // `...(cond ? { get maxDepth() {...} } : {})` INVOKES the getter and copies
    // its value, turning a live accessor into a property frozen to whichever
    // texture was current at construction — precisely the capture
    // `FloodSolverOutputs` warns against. It fails silently and only on odd
    // substep counts, because an even count returns parity to A and hides it.
    // `enumerable: true` keeps presence-as-capability working for
    // `Object.keys`/`hasOwnProperty`.
    const defineOutput = (key: keyof FloodSolverOutputs, get: () => THREE.Texture) => {
      Object.defineProperty(outputs, key, { get, enumerable: true })
    }
    if (this.analysis.maxDepth) {
      defineOutput('maxDepth', () => (this.currentIsA ? this.maxDepthA! : this.maxDepthB!))
    }
    if (this.analysis.arrivalTime) {
      defineOutput('arrivalTime', () => (this.currentIsA ? this.arrivalTimeA! : this.arrivalTimeB!))
    }
    if (this.analysis.duration) {
      defineOutput('duration', () => (this.currentIsA ? this.durationA! : this.durationB!))
    }
    if (this.analysis.flux) {
      // No ping-pong — a single texture, always current.
      defineOutput('flux', () => this.fluxTexture!)
    }
    this.outputsObject = outputs
  }

  /**
   * V2-C2 transport-only kernel body, transcribed from the A4 probe kernel
   * (FloodV2Probes.vue `pass2`) and `referencePass2`
   * (conservative-two-pass-solver-v2-reference.ts) — same channel mapping,
   * same boundary masks, same N,S,E,W accumulation order, same single
   * coefficient application. Extracted to a method (was a constructor-local
   * closure before V2-E1) purely so `ensureValidationResources()` can call
   * it after construction; the body itself is untouched — same operations,
   * same order, now referencing `this.transferTextureRef`/
   * `this.conductanceUniform`/etc. instead of constructor-local `const`s
   * that pointed at the identical objects.
   */
  private buildPass2TransportOnlyKernel(readDepth: THREE.Texture, writeTarget: THREE.StorageTexture): ComputeKernel {
    const gridSize = this.gridSize
    const transfer = this.transferTextureRef
    const conductanceUniform = this.conductanceUniform
    const cellSizeUniform = this.cellSizeUniform
    const dtUniform = this.dtUniform
    return buildComputeKernel(() => {
      const cell = cellOfIndex(instanceIndex, gridSize)
      const x = cell.x
      const y = cell.y

      const h = loadCell(readDepth, ivec2(x, y)).toVar()
      const out = textureLoad(transfer, ivec2(x, y)).toVar()

      const leftX = clampCellComponent(x.sub(1), gridSize)
      const rightX = clampCellComponent(x.add(1), gridSize)
      const bottomY = clampCellComponent(y.sub(1), gridSize)
      const topY = clampCellComponent(y.add(1), gridSize)

      // Explicit closed boundary at the RECEIVING cell's own edge —
      // independent of whatever PASS 1 stored there. Not a "defensive"
      // extra: PASS 1 zeroing its OWN outgoing across a boundary does not by
      // itself stop the classic failure, where a clamped neighbour lookup at
      // x=0 aliases to the cell ITSELF and its `.g` (east) channel — its own
      // real eastward donation — would be read as incoming-from-west. Same
      // branchless step() form as PASS 1: `finite * 0` is exactly 0, and it
      // keeps texture reads out of a TSL if/else arm.
      const fx = x.toFloat()
      const fy = y.toFloat()
      const last = float(gridSize - 1)
      const maskNorth = step(float(1), last.sub(fy))
      const maskSouth = step(float(1), fy)
      const maskEast = step(float(1), last.sub(fx))
      const maskWest = step(float(1), fx)

      // HARD CONTRACT channel mapping (r=N g=E b=S a=W): incoming from a
      // direction reads that NEIGHBOUR's OPPOSITE channel. Written out
      // explicitly, one line per direction — a generic "opposite channel"
      // helper is exactly the kind of abstraction that could hide an
      // axis-transposition mistake, which is the one error class this
      // mapping cannot recover from silently.
      const incomingFromNorth = textureLoad(transfer, ivec2(x, topY)).b.mul(maskNorth)
      const incomingFromSouth = textureLoad(transfer, ivec2(x, bottomY)).r.mul(maskSouth)
      const incomingFromEast = textureLoad(transfer, ivec2(rightX, y)).a.mul(maskEast)
      const incomingFromWest = textureLoad(transfer, ivec2(leftX, y)).g.mul(maskWest)

      const netNorth = incomingFromNorth.sub(out.r)
      const netSouth = incomingFromSouth.sub(out.b)
      const netEast = incomingFromEast.sub(out.g)
      const netWest = incomingFromWest.sub(out.a)

      // Exact accumulation order proven by A4/C1: N, S, E, W, left-associated
      // — ONE chained expression, not a reordered/re-associated sum.
      const netExchange = netNorth.add(netSouth).add(netEast).add(netWest)

      // SAFETY clamps only (C1's proof): PASS 1's donor-global limiter
      // guarantees `coefficient * outgoing <= h`, so `dh` never reaches the
      // `-h` bound in any tested scenario, and `h + dh` never goes negative.
      // Retained exactly as C1's reference retains them — not to repair
      // ordinary donor overdraw, which PASS 1 already prevents structurally.
      const dh = tslMax(
        conductanceUniform.mul(dtUniform).mul(netExchange).div(cellSizeUniform.mul(cellSizeUniform)),
        h.negate(),
      )
      const nextDepth = tslMax(0, h.add(dh))
      textureStore(writeTarget, uvec2(x, y), vec4(nextDepth, 0, 0, 1)).toWriteOnly()
    }, gridSize * gridSize)
  }

  /**
   * V2-C3/C4 full (transport + rainfall + capped infiltration) kernel body,
   * transcribed verbatim from `referencePass2`
   * (conservative-two-pass-solver-v2-reference.ts) — same ordering, same
   * `min`/`max` shape, no algebraic simplification. Shared by BOTH the
   * eager production targets (`pass2FullAtoB`/`pass2FullBtoA`, built in the
   * constructor) and the lazy C3 validation targets (`pass2FullFromA/B`,
   * built by `ensureValidationResources()`) — one body, two write targets,
   * exactly as before V2-E1's lifecycle split.
   *
   * `rainfallDepthM`/`infiltrationDepthM` are already-integrated depths for
   * ONE physical substep (matching `FloodForcing`) — never multiplied by
   * `dtSeconds` here, and applied exactly once per dispatch of this kernel.
   *
   * PASS 1's donor-global limiter caps outgoing depth against the OLD
   * physical depth only (rainfall is not yet added when transport is
   * evaluated), so rainfall never affects transport availability — it only
   * becomes available afterwards, for infiltration to consume.
   */
  private buildPass2FullKernel(readDepth: THREE.Texture, writeTarget: THREE.StorageTexture): ComputeKernel {
    const gridSize = this.gridSize
    const transfer = this.transferTextureRef
    const conductanceUniform = this.conductanceUniform
    const cellSizeUniform = this.cellSizeUniform
    const dtUniform = this.dtUniform
    const rainfallUniform = this.rainfallUniform
    const infiltrationUniform = this.infiltrationUniform
    return buildComputeKernel(() => {
      const cell = cellOfIndex(instanceIndex, gridSize)
      const x = cell.x
      const y = cell.y

      const h = loadCell(readDepth, ivec2(x, y)).toVar()
      const out = textureLoad(transfer, ivec2(x, y)).toVar()

      const leftX = clampCellComponent(x.sub(1), gridSize)
      const rightX = clampCellComponent(x.add(1), gridSize)
      const bottomY = clampCellComponent(y.sub(1), gridSize)
      const topY = clampCellComponent(y.add(1), gridSize)

      const fx = x.toFloat()
      const fy = y.toFloat()
      const last = float(gridSize - 1)
      const maskNorth = step(float(1), last.sub(fy))
      const maskSouth = step(float(1), fy)
      const maskEast = step(float(1), last.sub(fx))
      const maskWest = step(float(1), fx)

      const incomingFromNorth = textureLoad(transfer, ivec2(x, topY)).b.mul(maskNorth)
      const incomingFromSouth = textureLoad(transfer, ivec2(x, bottomY)).r.mul(maskSouth)
      const incomingFromEast = textureLoad(transfer, ivec2(rightX, y)).a.mul(maskEast)
      const incomingFromWest = textureLoad(transfer, ivec2(leftX, y)).g.mul(maskWest)

      const netNorth = incomingFromNorth.sub(out.r)
      const netSouth = incomingFromSouth.sub(out.b)
      const netEast = incomingFromEast.sub(out.g)
      const netWest = incomingFromWest.sub(out.a)
      const netExchange = netNorth.add(netSouth).add(netEast).add(netWest)

      // SAFETY clamp only — PASS 1's limiter guarantees this bound is never
      // reached (see `buildPass2TransportOnlyKernel`).
      const dh = tslMax(
        conductanceUniform.mul(dtUniform).mul(netExchange).div(cellSizeUniform.mul(cellSizeUniform)),
        h.negate(),
      )

      // Transport, then rainfall, then infiltration — V1's literal ordering,
      // preserved exactly (donor-cell-solver-v1.ts:240).
      const transported = h.add(dh)
      const afterRain = transported.add(rainfallUniform)

      // THE V2 CORRECTION: infiltration is capped to what is physically
      // available post-rain, instead of V1's unconditional subtraction
      // hidden behind a final `max(0, ...)` that silently overstates how
      // much was actually removed (V2-A's D2 finding: 199x overstatement).
      const availableForInfiltration = tslMax(float(0), afterRain)
      const actualInfiltration = tslMin(infiltrationUniform, availableForInfiltration)

      // Numerical safety only — `min()` already guarantees the subtrahend
      // cannot exceed `afterRain`, so this is not expected to bind.
      const nextDepth = tslMax(float(0), afterRain.sub(actualInfiltration))
      textureStore(writeTarget, uvec2(x, y), vec4(nextDepth, 0, 0, 1)).toWriteOnly()
    }, gridSize * gridSize)
  }

  /**
   * V2-E1. Lazily builds the C2/C3 validation-only resource group — exactly
   * once, on first use by any of `runPass2TransportOnly`/`runPass2Full`/
   * `readTransportOnlyDepthBuffer`/`readSourceSinkDepthBuffer`. Idempotent:
   * a second call is a no-op (checked via `transportOnlyOut`, which is
   * always created together with everything else in this group, so its
   * presence is a reliable proxy for "already initialized").
   *
   * Never called from the constructor, `advance()`, `reset()`, or the
   * `outputs`/`depthTexture` getters — those stay production-only.
   */
  private ensureValidationResources(): void {
    if (this.transportOnlyOut) return
    const gridSize = this.gridSize

    const makeValidationDepthTexture = (name: string) => {
      // Same policy as the production depth textures — plain R32Float depth
      // field, no mip chain, clamp-to-edge.
      const tex = new THREE.StorageTexture(gridSize, gridSize)
      tex.name = name
      tex.format = THREE.RedFormat
      tex.type = THREE.FloatType
      tex.generateMipmaps = false
      tex.minFilter = THREE.NearestFilter
      tex.magFilter = THREE.NearestFilter
      tex.wrapS = THREE.ClampToEdgeWrapping
      tex.wrapT = THREE.ClampToEdgeWrapping
      return tex
    }

    this.transportOnlyOut = makeValidationDepthTexture('V2TransportOnlyOut')
    this.sourceSinkOut = makeValidationDepthTexture('V2SourceSinkOut')

    this.pass2TransportOnlyFromA = this.buildPass2TransportOnlyKernel(this.depthTextureA, this.transportOnlyOut)
    this.pass2TransportOnlyFromB = this.buildPass2TransportOnlyKernel(this.depthTextureB, this.transportOnlyOut)
    this.pass2FullFromA = this.buildPass2FullKernel(this.depthTextureA, this.sourceSinkOut)
    this.pass2FullFromB = this.buildPass2FullKernel(this.depthTextureB, this.sourceSinkOut)
  }

  /** The ping-pong texture holding the latest depth. */
  get depthTexture(): THREE.Texture {
    return this.currentIsA ? this.depthTextureA : this.depthTextureB
  }

  /** Static hydraulic floor. Referenced here; the caller owns and disposes it. */
  get bedTexture(): THREE.Texture {
    return this.bedTextureRef
  }

  /** @see FloodSolver.outputs — V2 publishes depth only, honestly: no flux/maxDepth/arrivalTime/duration are computed yet. */
  get outputs(): FloodSolverOutputs {
    return this.outputsObject
  }

  /**
   * INTERNAL numerical state, exposed for validation only.
   *
   * Not published through `FloodSolverOutputs` and not an engineering output:
   * the stored quantity is an unscaled solver term in m^2, not a per-step
   * transfer depth, and presenting it as `outputs.flux` would make that
   * contract misleading. Whether to expose a scaled quantity is a decision for
   * after V2 validates.
   */
  get transferTexture(): THREE.Texture {
    return this.transferTextureRef
  }

  /**
   * Phase-3 analysis state, exposed for VALIDATION ONLY — same status as
   * `transferTexture` above, and deliberately not the public contract.
   *
   * `outputs` is where consumers read analysis fields, and a key appears there
   * only once the field's update kernel exists and is proven. This getter lets
   * the 3B gates verify seeding before any such kernel is written, without
   * advertising a capability that does not work yet. Each entry is the CURRENT
   * ping-pong side, matching how `outputs.depth` behaves; `undefined` means the
   * capability is off.
   */
  get analysisTexturesForValidation(): {
    maxDepth?: THREE.Texture
    arrivalTime?: THREE.Texture
    duration?: THREE.Texture
    flux?: THREE.Texture
    elapsedSimSeconds: number
  } {
    return {
      maxDepth: this.currentIsA ? this.maxDepthA : this.maxDepthB,
      arrivalTime: this.currentIsA ? this.arrivalTimeA : this.arrivalTimeB,
      duration: this.currentIsA ? this.durationA : this.durationB,
      flux: this.fluxTexture,
      elapsedSimSeconds: this.elapsedSimSeconds,
    }
  }

  /**
   * The one-time seeding group: depth plus whichever analysis fields exist.
   *
   * `seedDepth` itself is untouched by Phase 3 — each analysis field brings its
   * own kernel instead, so no single kernel accumulates write targets. All of
   * them land in the same batch, keeping "everything is seeded together on the
   * first dispatch" true for analysis state as well as depth.
   */
  private seedBatch(): unknown[] {
    if (this.depthSeeded) return []
    this.depthSeeded = true
    const batch: unknown[] = [this.seedDepth]
    if (this.seedMaxDepth) batch.push(this.seedMaxDepth)
    if (this.seedArrivalTime) batch.push(this.seedArrivalTime)
    if (this.seedDuration) batch.push(this.seedDuration)
    if (this.clearFlux) batch.push(this.clearFlux)
    return batch
  }

  /**
   * Runs PASS 1 against the current depth texture.
   *
   * Deliberately NOT named `advance`: nothing advances. Depth is unchanged and
   * the ping-pong parity does not flip, because the flip belongs with the write
   * PASS 2 will perform. Calling this twice simply recomputes the same transfer
   * field from the same state.
   */
  runPass1(webgpuRenderer: THREE.WebGPURenderer, dtSeconds: number): void {
    this.dtUniform.value = dtSeconds
    const batch: unknown[] = [...this.seedBatch()]
    batch.push(this.currentIsA ? this.pass1FromA : this.pass1FromB)
    webgpuRenderer.compute(batch as Parameters<THREE.WebGPURenderer['compute']>[0])
  }

  /**
   * V2-C2 VALIDATION ONLY. Runs PASS 1 then transport-only PASS 2 in one
   * submission, writing the result to `transportOnlyOut` — NOT to
   * `depthTextureA/B`. Deliberately does not flip `currentIsA`: this is a
   * controlled one-step transport probe, not a step of the solver.
   *
   * Not named `advance()` and not exposed as anything resembling the
   * `FloodSolver` contract — that placeholder-shaped landmine is exactly
   * what earlier phases refused to create before the contract can be
   * honoured for real (rainfall, infiltration, ping-pong stepping — C3/C4).
   */
  runPass2TransportOnly(webgpuRenderer: THREE.WebGPURenderer, dtSeconds: number): void {
    this.ensureValidationResources()
    this.dtUniform.value = dtSeconds
    const batch: unknown[] = [...this.seedBatch()]
    batch.push(this.currentIsA ? this.pass1FromA : this.pass1FromB)
    batch.push(this.currentIsA ? this.pass2TransportOnlyFromA! : this.pass2TransportOnlyFromB!)
    webgpuRenderer.compute(batch as Parameters<THREE.WebGPURenderer['compute']>[0])
  }

  /**
   * V2-C3 VALIDATION ONLY. Runs PASS 1 then FULL (transport + rainfall +
   * capped infiltration) PASS 2 in one submission, writing to
   * `sourceSinkOut` — NOT to `depthTextureA/B`, and does not flip
   * `currentIsA`: still a controlled one-step probe, not a step of the
   * solver. `forcing` is already-integrated depth for this one substep and
   * is applied exactly once, matching `FloodForcing`.
   */
  runPass2Full(
    webgpuRenderer: THREE.WebGPURenderer,
    dtSeconds: number,
    forcing: FloodForcing,
  ): void {
    this.ensureValidationResources()
    this.dtUniform.value = dtSeconds
    this.rainfallUniform.value = forcing.rainfallDepthM
    this.infiltrationUniform.value = forcing.infiltrationDepthM
    const batch: unknown[] = [...this.seedBatch()]
    batch.push(this.currentIsA ? this.pass1FromA : this.pass1FromB)
    batch.push(this.currentIsA ? this.pass2FullFromA! : this.pass2FullFromB!)
    webgpuRenderer.compute(batch as Parameters<THREE.WebGPURenderer['compute']>[0])
  }

  /**
   * V2-C4 — PRODUCTION TIMESTEP. One physical substep is EXACTLY two compute
   * dispatches — PASS 1, then full PASS 2 writing directly to the opposite
   * depth texture — and exactly one ping-pong flip, after PASS 2. No depth
   * copy, no third dispatch, no validation texture in this path.
   *
   * Matches V1's audited `advance()` contract exactly (donor-cell-solver-v1.ts:279-300):
   * `dtSeconds`/`rainfallDepthM`/`infiltrationDepthM` are set ONCE before the
   * loop and reused, unscaled, for every one of `substeps` physical steps —
   * `substeps=N` means N full physical steps at the SAME supplied dt and the
   * SAME supplied already-integrated forcing, not dt or forcing divided by N.
   * `substeps<=0` (or NaN) makes the loop body never run, exactly like V1: a
   * no-op on depth and ping-pong parity (uniforms are still assigned, as V1's
   * are, since nothing consumes them without a dispatch). V1 does not
   * validate `substeps` beyond that loop condition, so neither does this.
   *
   * The loop performs NO allocation, NO `await`, and NO readback — GPU state
   * evolution only, submitted in one `compute()` call, mirroring V1's
   * synchronous hot-loop discipline.
   */
  advance(
    webgpuRenderer: THREE.WebGPURenderer,
    dtSeconds: number,
    forcing: FloodForcing,
    substeps = 1,
  ): void {
    this.dtUniform.value = dtSeconds
    this.rainfallUniform.value = forcing.rainfallDepthM
    this.infiltrationUniform.value = forcing.infiltrationDepthM

    // Seed must precede the first physics kernel; array order is execution order.
    const batch: unknown[] = [...this.seedBatch()]

    // Parity is tracked in a LOCAL while the batch is assembled, and only
    // written back to `this.currentIsA` once the whole call is built.
    //
    // This matters more than it looks. Every analysis kernel for a substep is
    // selected by the parity that substep STARTED on — flipping the
    // authoritative field midway through appending a group would select the
    // wrong sides for the kernels that follow it, and the failure would be
    // silent: correct-looking magnitudes written to the texture the next
    // substep is about to overwrite.
    // A uniform's GPU buffer is written once per compute() submission, so every
    // dispatch inside one submission sees the same final value — the reason
    // `substeps > 1` is documented as constant-forcing-only. arrivalTime is the
    // one field needing a value that DIFFERS between substeps of a single call
    // (the absolute time at the moment of crossing), so when it is enabled the
    // call is split into one submission per physical substep, with
    // `elapsedTimeUniform` updated between them.
    //
    // Dispatch order and read/write dependencies are identical either way —
    // WebGPU preserves in-order execution across submissions on one queue — so
    // this changes submission count and nothing else. It must never move a
    // depth bit, and 3D/3G check exactly that. It also costs production
    // nothing: Flood3DView already calls advance() once per physical substep.
    const perSubstepSubmission = this.analysis.arrivalTime && substeps > 1

    const submit = (nodes: unknown[]) => {
      // Renderer.compute([]) throws '.compute() expects a ComputeNode', and a
      // no-op call should be a no-op rather than an exception. Guarding on the
      // batch rather than on `substeps` matters: a fresh or just-reset solver
      // still owes its seed dispatch even at zero substeps.
      if (nodes.length === 0) return
      webgpuRenderer.compute(nodes as Parameters<THREE.WebGPURenderer['compute']>[0])
    }

    let stepStartsOnA = this.currentIsA
    for (let i = 0; i < substeps; i += 1) {
      // The clock advances per PHYSICAL substep, from an f64 accumulator, so
      // variable dt across calls stays exact and nothing depends on a step
      // index times an assumed-constant dt. The value the texture receives is
      // necessarily fp32(elapsed + dt); that rounding is the published
      // representation, which is why references compare against Math.fround.
      if (this.elapsedTimeUniform) {
        this.elapsedSimSeconds += dtSeconds
        this.elapsedTimeUniform.value = this.elapsedSimSeconds
      }

      if (stepStartsOnA) {
        // Analysis observes what PASS 2 just produced; it never writes depth,
        // so it cannot perturb the physics regardless of what it reads.
        batch.push(this.pass1FromA, this.pass2FullAtoB)
        if (this.maxDepthAtoB) batch.push(this.maxDepthAtoB)
        if (this.arrivalAtoB) batch.push(this.arrivalAtoB)
        if (this.durationAtoB) batch.push(this.durationAtoB)
      } else {
        batch.push(this.pass1FromB, this.pass2FullBtoA)
        if (this.maxDepthBtoA) batch.push(this.maxDepthBtoA)
        if (this.arrivalBtoA) batch.push(this.arrivalBtoA)
        if (this.durationBtoA) batch.push(this.durationBtoA)
      }
      // Reference/state selection only — never a texture copy. Flips exactly
      // once per physical substep, after the COMPLETE group is pushed.
      stepStartsOnA = !stepStartsOnA

      if (perSubstepSubmission) {
        submit(batch.splice(0, batch.length))
      }
    }
    // Depth and every enabled analysis field now share this parity.
    this.currentIsA = stepStartsOnA

    // Flux reflects the FINAL physical substep of this call, and runs ONCE —
    // never per substep — because `transferTextureRef` is fully overwritten by
    // every PASS-1 dispatch with no ping-pong, so whatever it holds after the
    // loop above IS the final substep's transport data already. Gated on
    // `substeps > 0`: converting on a substeps=0 call would either read stale
    // data from a previous call (misleading) or, on a first call, the
    // WebGPU-guaranteed zero-initialized texture (harmless but pointless) —
    // neither is "the final physical substep of THIS call", which is the
    // documented contract.
    if (this.fluxConversion && substeps > 0) batch.push(this.fluxConversion)

    // Whole-call submission, matching V1's batching, unless the loop above
    // already drained it one substep at a time.
    submit(batch)
  }

  /**
   * Reads the CURRENT depth texture (the live ping-pong getter) back to the
   * CPU. R32Float, same de-padding as V1's `readDepthBuffer`. Validation/dev
   * use only — never on an animation path.
   */
  async readDepthBuffer(webgpuRenderer: THREE.WebGPURenderer): Promise<Float32Array> {
    if (!this.depthSeeded) {
      throw new Error('ConservativeTwoPassSolverV2.readDepthBuffer: no compute dispatched yet — depth textures are unseeded.')
    }
    const size = this.gridSize
    const backend = (webgpuRenderer as unknown as {
      backend: { copyTextureToBuffer(t: THREE.Texture, x: number, y: number, w: number, h: number, f: number): Promise<Float32Array> }
    }).backend
    const raw = await backend.copyTextureToBuffer(this.depthTexture, 0, 0, size, size, 0)

    const BYTES_PER_TEXEL = 4 // r32float
    const paddedBytesPerRow = Math.ceil((size * BYTES_PER_TEXEL) / 256) * 256
    const floatsPerPaddedRow = paddedBytesPerRow / 4
    if (floatsPerPaddedRow === size) return Float32Array.from(raw.subarray(0, size * size))
    const out = new Float32Array(size * size)
    for (let row = 0; row < size; row += 1) {
      const src = row * floatsPerPaddedRow
      out.set(raw.subarray(src, src + size), row * size)
    }
    return out
  }

  /**
   * Reads the transfer texture back for validation. RGBA32Float, so 16 B/texel
   * and rows padded to 256 bytes. NEVER on an animation path.
   */
  async readTransferBuffer(webgpuRenderer: THREE.WebGPURenderer): Promise<Float32Array> {
    if (!this.depthSeeded) {
      throw new Error('ConservativeTwoPassSolverV2.readTransferBuffer: PASS 1 has not been dispatched yet.')
    }
    const size = this.gridSize
    const backend = (webgpuRenderer as unknown as {
      backend: { copyTextureToBuffer(t: THREE.Texture, x: number, y: number, w: number, h: number, f: number): Promise<Float32Array> }
    }).backend
    const raw = await backend.copyTextureToBuffer(this.transferTextureRef, 0, 0, size, size, 0)

    const BYTES_PER_TEXEL = 16 // rgba32float
    const paddedBytesPerRow = Math.ceil((size * BYTES_PER_TEXEL) / 256) * 256
    const floatsPerPaddedRow = paddedBytesPerRow / 4
    const out = new Float32Array(size * size * 4)
    for (let row = 0; row < size; row += 1) {
      const src = row * floatsPerPaddedRow
      out.set(raw.subarray(src, src + size * 4), row * size * 4)
    }
    return out
  }

  /**
   * V2-C2 VALIDATION ONLY. Reads `transportOnlyOut` back — R32Float, so
   * 4 B/texel and rows padded to 256 bytes at small grid sizes. Requires
   * `runPass2TransportOnly` to have run at least once.
   */
  async readTransportOnlyDepthBuffer(webgpuRenderer: THREE.WebGPURenderer): Promise<Float32Array> {
    if (!this.depthSeeded) {
      throw new Error('ConservativeTwoPassSolverV2.readTransportOnlyDepthBuffer: PASS 2 has not been dispatched yet.')
    }
    // Validation API: ensures the lazy resource group exists rather than
    // returning bogus/uninitialized data if called before any run*() call.
    this.ensureValidationResources()
    const size = this.gridSize
    const backend = (webgpuRenderer as unknown as {
      backend: { copyTextureToBuffer(t: THREE.Texture, x: number, y: number, w: number, h: number, f: number): Promise<Float32Array> }
    }).backend
    const raw = await backend.copyTextureToBuffer(this.transportOnlyOut!, 0, 0, size, size, 0)

    const BYTES_PER_TEXEL = 4 // r32float
    const paddedBytesPerRow = Math.ceil((size * BYTES_PER_TEXEL) / 256) * 256
    const floatsPerPaddedRow = paddedBytesPerRow / 4
    if (floatsPerPaddedRow === size) return Float32Array.from(raw.subarray(0, size * size))
    const out = new Float32Array(size * size)
    for (let row = 0; row < size; row += 1) {
      const src = row * floatsPerPaddedRow
      out.set(raw.subarray(src, src + size), row * size)
    }
    return out
  }

  /**
   * V2-C3 VALIDATION ONLY. Reads `sourceSinkOut` back — R32Float, same
   * de-padding as `readTransportOnlyDepthBuffer`. Requires `runPass2Full` to
   * have run at least once.
   */
  async readSourceSinkDepthBuffer(webgpuRenderer: THREE.WebGPURenderer): Promise<Float32Array> {
    if (!this.depthSeeded) {
      throw new Error('ConservativeTwoPassSolverV2.readSourceSinkDepthBuffer: PASS 2 has not been dispatched yet.')
    }
    // Validation API: ensures the lazy resource group exists rather than
    // returning bogus/uninitialized data if called before any run*() call.
    this.ensureValidationResources()
    const size = this.gridSize
    const backend = (webgpuRenderer as unknown as {
      backend: { copyTextureToBuffer(t: THREE.Texture, x: number, y: number, w: number, h: number, f: number): Promise<Float32Array> }
    }).backend
    const raw = await backend.copyTextureToBuffer(this.sourceSinkOut!, 0, 0, size, size, 0)

    const BYTES_PER_TEXEL = 4 // r32float
    const paddedBytesPerRow = Math.ceil((size * BYTES_PER_TEXEL) / 256) * 256
    const floatsPerPaddedRow = paddedBytesPerRow / 4
    if (floatsPerPaddedRow === size) return Float32Array.from(raw.subarray(0, size * size))
    const out = new Float32Array(size * size)
    for (let row = 0; row < size; row += 1) {
      const src = row * floatsPerPaddedRow
      out.set(raw.subarray(src, src + size), row * size)
    }
    return out
  }

  /**
   * Returns depth — and every analysis field — to the initial condition.
   *
   * LAZY, exactly as it has always been: this arms re-seeding, it does not
   * dispatch. The textures still hold the previous run's values until the next
   * `advance()` runs the seed batch (which `advance(..., 0)` does too). The
   * clock is the one piece of state reset eagerly, because it is a CPU field
   * and costs nothing.
   */
  reset(): void {
    this.depthSeeded = false
    this.currentIsA = true
    this.elapsedSimSeconds = 0
    if (this.elapsedTimeUniform) this.elapsedTimeUniform.value = 0
  }

  /** Disposes ONLY solver-owned resources. `bedTexture` belongs to the caller. */
  dispose(): void {
    this.depthTextureA.dispose()
    this.depthTextureB.dispose()
    this.seedTexture.dispose()
    this.transferTextureRef.dispose()
    // Phase 3 analysis fields — optional chaining for the same reason as the
    // validation resources below: absent entirely when the capability is off,
    // so there is nothing to null-check around.
    this.maxDepthA?.dispose()
    this.maxDepthB?.dispose()
    this.arrivalTimeA?.dispose()
    this.arrivalTimeB?.dispose()
    this.durationA?.dispose()
    this.durationB?.dispose()
    this.fluxTexture?.dispose()
    // Validation-only, lazily created — absent entirely on a normal instance
    // that never called a validation API. Optional chaining, not a
    // null-check dance: there is nothing to dispose if nothing was ever
    // allocated.
    this.transportOnlyOut?.dispose()
    this.sourceSinkOut?.dispose()
  }
}
